import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Compass, Loader2, ExternalLink } from 'lucide-react';
import { pathways } from '@/lib/api';
import { Link } from 'react-router-dom';

/**
 * PathwayAdvisorCard — compact, always-visible summary of the applicant's
 * migration lifecycle on the Matter detail page. Prompts users to run the
 * Pathway Advisor when no analysis exists, and surfaces the saved verdicts
 * so users can see the applicant's journey at a glance (not just this matter).
 */
export default function PathwayAdvisorCard({ matterId, matterRef }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!matterId) return;
    setLoading(true);
    pathways.getForMatter(matterId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [matterId]);

  const recs = data?.recommended || data?.results || [];
  const eligibleCount = recs.filter((r) => r.eligible === 'yes').length;
  const likelyCount = recs.filter((r) => r.eligible === 'likely').length;

  return (
    <Card className="border-[#1A3A5C]/15" data-testid="matter-pathway-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#1A3A5C]/5">
              <Compass className="h-5 w-5 text-[#1A3A5C]" />
            </div>
            <div>
              <div className="text-sm font-semibold text-[#1A3A5C]">Pathway Advisor · lifecycle view</div>
              <div className="mt-0.5 text-xs text-slate-500">
                Applicants are journeys, not single matters. See every visa this applicant qualifies for.
              </div>
            </div>
          </div>
          <Link to={`/app/pathways?matter=${matterId}`} data-testid="matter-pathway-open">
            <Button variant="outline" size="sm" className="text-xs">
              Open advisor <ExternalLink className="ml-1.5 h-3 w-3" />
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="mt-3 flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3 w-3 animate-spin" /> Loading pathway…</div>
        ) : data ? (
          <div className="mt-3">
            <div className="flex flex-wrap items-center gap-2">
              {data.points_estimate?.total != null && (
                <Badge className="border border-[#2ECC9A]/40 bg-[#2ECC9A]/10 text-[#1A3A5C]">
                  {data.points_estimate.total} pts
                </Badge>
              )}
              <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700 text-[11px]">
                {eligibleCount} eligible
              </Badge>
              <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 text-[11px]">
                {likelyCount} likely
              </Badge>
              {data.saved_at && (
                <span className="text-[10px] text-slate-400">Saved {new Date(data.saved_at).toLocaleDateString('en-AU')}</span>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="matter-pathway-recs">
              {recs.slice(0, 8).map((r) => {
                const tone = r.eligible === 'yes' ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                  : r.eligible === 'likely' ? 'border-amber-300 bg-amber-50 text-amber-800'
                  : 'border-slate-200 bg-slate-50 text-slate-600';
                return (
                  <Badge key={r.visa} variant="outline" className={`${tone} text-[11px]`} data-testid={`matter-pathway-rec-${r.visa}`}>
                    {r.visa} · {r.eligible}
                  </Badge>
                );
              })}
              {recs.length === 0 && (
                <div className="text-xs text-slate-500">No visa verdicts saved yet.</div>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-3 rounded-md border border-dashed border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            No pathway analysis yet for matter <span className="font-mono">{matterRef}</span>.
            <Link to={`/app/pathways?matter=${matterId}`} className="ml-1 text-[#2ECC9A] hover:underline" data-testid="matter-pathway-run">
              Run advisor →
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
